import React, { useMemo, useState, useEffect } from 'react';
import DenyOrderModal from 'components/Modal/DenyOrderModal';
import OrderDetailsModal from 'components/OrderDetailsModal';
import DealEnum from 'model/DealEnum';
import { currencyFormat } from 'services/utils';
import AccountContext from 'storage/AccountContext';
import OrderActionsContext from 'storage/OrderActionsContext';
import PendingOrdersContext from 'storage/PendingOrdersContext';
import { canBeAutoApproved } from 'utils/orders';
import { createMultiSortClickHandler, getSortIndicator, backendFormatToMultiSort, type SortField } from 'utils/sorting';
import PendingOrdersViewRow from './PendingOrdersViewRow';

function PendingOrdersList({
  numOfResults,
  orders,
  totalOrders,
  updateOrdersList,
}: {
  orders: WoaRequest[];
  updateOrdersList: () => void;
  numOfResults: number;
  totalOrders: number;
}) {
  const headers = [
    { field: 'view', title: '', sortable: false },
    { field: 'createdDate', title: 'Request Date', sortable: true },
    { field: 'author', title: 'RCA Name', sortable: true },
    { field: 'journalName', title: 'Journal', sortable: true },
    { field: 'allocatedAmount', title: 'Amount', sortable: true },
    { field: 'revenueModel', title: 'Revenue Model', sortable: true },
    { field: 'article.woadPublicationStatus', title: 'Publication Status', sortable: true },
  ];

  const { selectedAccount } = AccountContext.useContext();
  const { denyOrder } = OrderActionsContext.useContext();
  const { accountId, getPendingOrdersList, keyword, sortBy, sortDirection, updateSorting } =
    PendingOrdersContext.useContext();

  const currentSortFields: SortField[] = backendFormatToMultiSort(sortBy, sortDirection);

  const [viewDetailsOrder, setViewDetailsOrder] = useState<number>();
  const [denyOrderId, setDenyOrderId] = useState<number>();

  const onView = order => setViewDetailsOrder(order.id);
  const onClickDeny = order => setDenyOrderId(order.id);
  const closeDetails = () => setViewDetailsOrder(null);
  const closeDenyOrderModal = () => setDenyOrderId(null);

  const onSubmitDeny = reason => {
    denyOrder(denyOrderId, { reason });
    closeDenyOrderModal();
  };

  const summary = useMemo<number>(
    () => orders.reduce((accumulator, x) => accumulator + parseFloat(String(x.allocatedAmount)), 0),
    [orders],
  );

  const handleSortingUpdate = (sortBy: string | null, sortDirection: string | null) => {
    updateSorting(sortBy, sortDirection);
    if (accountId) {
      getPendingOrdersList(accountId, { keyword, sortBy, sortDirection }, 0);
    }
  };

  const onClickSort = createMultiSortClickHandler(headers, currentSortFields, handleSortingUpdate);

  return (
    <>
      {!!viewDetailsOrder && (
        <OrderDetailsModal close={closeDetails} orderId={viewDetailsOrder} showPrice={selectedAccount.showPrice} />
      )}
      {!!denyOrderId && <DenyOrderModal onCancel={closeDenyOrderModal} onDeny={onSubmitDeny} />}
      <table className="table pendingOrdersView">
        <thead>
          <tr className="pendingOrdersView-headRow" style={{ textAlign: 'center' }}>
            {headers
              .filter(header => header.field !== 'revenueModel' || selectedAccount.dealType === DealEnum.BOTH)
              .map(header => {
                const { direction, order } = getSortIndicator(header.field, currentSortFields);

                return (
                  <th key={header.field} className="ordersView-headCell">
                    {header.sortable ? (
                      <button
                        className="btn btn-link text-reset p_0 bold"
                        data-field={header.field}
                        data-seleniumid={`pendingOrdersView-sort--${header.field}`}
                        onClick={onClickSort}
                        style={{ fontSize: `1em` }}
                        title="Click to sort: asc -> desc -> none"
                        type="button"
                      >
                        {header.title}
                        {direction && (
                          <>
                            <span className={direction === 'asc' ? 'm--sortAsc' : 'm--sortDesc'} />
                            {order && <small className="ml-1">{order}</small>}
                          </>
                        )}
                      </button>
                    ) : (
                      header.title
                    )}
                  </th>
                );
              })}
            <th style={{ width: '155px' }} />
          </tr>
        </thead>
        {orders.length > 0 && (
          <tbody>
            {orders.map(order => (
              <PendingOrdersViewRow
                key={order.id}
                autoApproval={canBeAutoApproved(order, selectedAccount)}
                currency={selectedAccount.currency}
                dealType={selectedAccount.dealType}
                onDeny={onClickDeny}
                onView={onView}
                order={order}
                permissions={selectedAccount.permissions}
                showPrice={selectedAccount.showPrice}
              />
            ))}
          </tbody>
        )}

        {orders.length > 0 && summary > 0 && (
          <tbody>
            <tr className="pendingOrdersView-dataRow">
              <td className="ta_r bold pr_5 pendingOrdersView-dataCell" colSpan={4}>
                Total:
              </td>
              <td
                className="bold pendingOrdersView-dataCell nobr"
                colSpan={3}
                data-seleniumid="pendingOrdersView-totalAmount"
              >
                {selectedAccount.showPrice ? currencyFormat(summary, selectedAccount.currency) : 'N/A'}
              </td>
            </tr>
          </tbody>
        )}
      </table>

      <div>
        {/* It is KOSTYL for react bug. Ask Denis Shelest about it */}
        {orders.length === 0 ? 'No request found' : ''}
      </div>
      {orders.length > 0 && orders.length < totalOrders && (
        <button
          className="btn btn-secondary btn-block mt_10"
          data-seleniumid="ordersView-seeMore"
          onClick={updateOrdersList}
          type="button"
        >
          {`See More (${numOfResults})`}
        </button>
      )}
    </>
  );
}

export default PendingOrdersList;
